import { formatAgeBucket } from '@/components/creator-discovery/utils';
import {
  formatBrandAffinityScore,
  formatEngagementRate,
} from '@/components/creator-discovery/utils/creator-formatters';
import { geoLabel } from '@/components/companion/preference-labels';
import { matchScoreToTier } from '@/components/recommendations/match-score-tier';
import {
  NICHE_VALUES,
  type AudienceInsights,
  type BrandCollaboration,
  type MatchTier,
  type Niche,
  type PlanScopedCreatorProfile,
  type PlanScopedContactSummaryDto,
} from '@fluenceur/types';
import type { TFunction } from 'i18next';
import type { ContactPlatform } from '../contact-drawer-url-state';
import { platformOf } from '../platform-of';
import { isStatsLocked } from '@/components/plan-locks/creator-locks';
import type {
  AudienceBar,
  ContactAudience,
  ContactProfile,
  ContactProfileFacts,
  ContactStat,
  ContactStatKind,
} from '../contact-types';

/**
 * Maps a live creator-api `CreatorProfile` into the per-platform `ContactProfile`
 * shape the existing PROFILE sub-components already consume (ContactStatCard,
 * ContactAudienceBars, ContactDistribution). Pure + single-purpose: no fetching,
 * no JSX. Display strings are formatted locale-aware ("84,2K", "6,2K€").
 */

/** Em-dash sentinel emitted when a metric has no measured value. */
export const NO_DATA_SENTINEL = '—';

const isFiniteNumber = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n);

/** Reach still uses its existing zero sentinel; averages are nullable. */
const nonZero = (n: number | null | undefined): number | null =>
  isFiniteNumber(n) && n !== 0 ? n : null;

/**
 * Decimal separator for the dashboard's two supported locales (fr / en).
 * Pre-compute once per stats build and pass to the formatter calls in that
 * build rather than recomputing inside each formatter call.
 */
export const decimalSeparator = (locale: string): string => (locale.startsWith('fr') ? ',' : '.');

/**
 * One decimal, dropped when it is a zero: "84,2" but "50", never "50,0". A
 * trailing zero adds a digit that says nothing and reads as false precision.
 */
const oneDecimal = (n: number, sep: string): string =>
  n.toFixed(1).replace(/\.0$/, '').replace('.', sep);

/**
 * Compact number — "84,2K", "1,2M", "50K" (fr) / "84.2K", "1.2M", "50K" (en).
 *
 * The product spec requires tight formatting without a space before the suffix
 * ("84,2K" not "84,2 k"). `Intl.NumberFormat` with `notation: "compact"` in
 * fr-FR produces a space-separated suffix that does not match the spec, so this
 * implementation keeps direct control over the output.
 *
 * Not aligned with `boost-insights-format` — that module has a different display
 * contract (absolute integers with locale grouping, no K/M suffix).
 */
export const formatCompactNumber = (n: number | null | undefined, locale: string): string => {
  if (!isFiniteNumber(n)) return NO_DATA_SENTINEL;
  const sep = decimalSeparator(locale);
  if (n >= 1_000_000) return `${oneDecimal(n / 1_000_000, sep)}M`;
  if (n >= 1_000) return `${oneDecimal(n / 1_000, sep)}K`;
  return n.toLocaleString(locale);
};

/**
 * Percent — "4,8%" (fr) / "4.8%" (en).
 *
 * Uses one decimal place. Same no-Intl rationale as `formatCompactNumber`.
 */
export const formatPercent = (ratio: number | null | undefined, locale: string): string => {
  const rate = formatEngagementRate(ratio);
  if (!rate) return NO_DATA_SENTINEL;
  const sep = decimalSeparator(locale);
  return rate.replace('.', sep);
};

/**
 * EMV currency — "6,2K€" / "2K€" / "128€" (fr) or "6.2K€" / "2K€" / "128€" (en).
 *
 * Rounds to whole euros for sub-1 000 values; uses K suffix above that. An
 * amount between 0 and 1 € reads "< 1€": rounding it would print "0€" or "1€",
 * and a zero reads as a measured nothing.
 */
const formatEmv = (n: number | null | undefined, locale: string): string => {
  if (!isFiniteNumber(n)) return NO_DATA_SENTINEL;
  const sep = decimalSeparator(locale);
  if (n >= 1_000) return `${oneDecimal(n / 1_000, sep)}K€`;
  if (n > 0 && n < 1) return '< 1€';
  return `${Math.round(n).toLocaleString(locale)}€`;
};

/** Prefix of a modelled figure; the no-break space keeps it on the value's line. */
const APPROXIMATELY = '≈\u00a0';

/**
 * The estimate tile. Its value reads as approximate ("≈ 250€") and its label
 * already says "estimated", so no badge is needed; the tooltip explains it with
 * the CPM the server used. A missing input reads "Not measured", never 0. A
 * partial card (the local mirror) was not read live, so the tile shows the same
 * unavailable state as reach and the averages, without approximation nor
 * explanation.
 */
const estimatedMediaValueStat = (
  profile: PlanScopedCreatorProfile,
  live: boolean,
  t: TFunction,
  locale: string
): ContactStat => {
  const label = t('contactDrawer.profile.statLabels.emv');
  if (!live) return { label, value: NO_DATA_SENTINEL };
  const estimate = profile.estimatedMediaValue;
  if (!estimate) {
    return { label, value: t('contactDrawer.profile.unmeasured'), muted: true };
  }
  const cpm = estimate.cpmEur.toLocaleString(locale);
  const amount = formatEmv(estimate.amount, locale);
  return {
    label,
    // "< 1€" already reads as inexact; "≈ < 1€" would say it twice.
    value: amount.startsWith('<') ? amount : `${APPROXIMATELY}${amount}`,
    tooltip:
      estimate.basis === 'tiktok_median_views'
        ? t('contactDrawer.profile.emvTooltipTikTok', { cpm })
        : t('contactDrawer.profile.emvTooltip', { cpm }),
  };
};

export const PLATFORM_LABEL: Record<ContactPlatform, string> = {
  ig: 'Instagram',
  tiktok: 'TikTok',
};

export const PLATFORM_BRAND_COLOR: Record<ContactPlatform, string> = {
  ig: '#E1306C',
  tiktok: '#111111',
};

export const PLATFORM_BASE_URL: Record<ContactPlatform, string> = {
  ig: 'https://instagram.com/',
  tiktok: 'https://tiktok.com/@',
};

/** Pick the largest-percentage bucket and render a "<label> · <pct>%" headline. */
const headline = (rows: AudienceBar[]): string => {
  if (rows.length === 0) return NO_DATA_SENTINEL;
  const top = rows.reduce((best, row) => (row.value > best.value ? row : best), rows[0]);
  return `${top.label} · ${Math.round(top.value)}%`;
};

const toBars = (rows: Array<{ label: string; percentage: number }> | undefined): AudienceBar[] =>
  (rows ?? []).map((row) => ({ label: row.label, value: Math.round(row.percentage) }));

/**
 * Round a two-sided percentage split (reach, gender), or report it as unmeasured.
 *
 * A real split always has one non-zero side, so an absent breakdown and a literal
 * 0/0 mean the same thing: the dataset never computed it. `reach` in particular
 * is only ever populated by the Meta insights path, never by Creator Discovery —
 * defaulting it to 0 would show every Discovery creator a fake "0% / 0%" bar.
 * Both sides go null together so the view has a single condition to test.
 */
const measuredSplit = (
  left: number | null | undefined,
  right: number | null | undefined
): [number, number] | [null, null] => {
  const leftPct = Math.round(left ?? 0);
  const rightPct = Math.round(right ?? 0);
  if (leftPct === 0 && rightPct === 0) return [null, null];
  return [leftPct, rightPct];
};

const mapAudience = (insights: AudienceInsights | undefined): ContactAudience => {
  const [reachFollowers, reachNonFollowers] = measuredSplit(
    insights?.reach?.followersPercentage,
    insights?.reach?.nonFollowersPercentage
  );
  const [female, male] = measuredSplit(
    insights?.genderDistribution?.female,
    insights?.genderDistribution?.male
  );

  const countries = toBars(
    insights?.countryDistribution?.map((c) => ({ label: c.countryName, percentage: c.percentage }))
  );
  const cities = toBars(
    insights?.cityDistribution?.map((c) => ({ label: c.cityName, percentage: c.percentage }))
  );

  return {
    reachFollowers,
    reachNonFollowers,
    female,
    male,
    countryHeadline: headline(countries),
    cityHeadline: headline(cities),
    countries,
    cities,
  };
};

/**
 * Detected brand partnerships, normalised for display: unnamed entries dropped,
 * duplicates collapsed case-insensitively. The same brand can be inferred from
 * several captions, and the name doubles as the list key, so a duplicate would
 * both repeat a row and collide on its React key. First occurrence wins.
 */
const toBrandCollaborations = (rows: BrandCollaboration[] | undefined): BrandCollaboration[] => {
  const seen = new Set<string>();
  return (rows ?? []).filter((row) => {
    const name = row.name?.trim();
    if (!name || seen.has(name.toLowerCase())) return false;
    seen.add(name.toLowerCase());
    return true;
  });
};

/**
 * Rail row summary ("84,2K abonnés · 4,8% ER"). The engagement clause is dropped
 * when the rate is unknown, so an un-enriched profile reads "84,2K abonnés".
 */
export const formatFollowersLabel = (
  followersCount: number | null | undefined,
  engagementRate: number | null | undefined,
  t: TFunction,
  locale: string
): string => {
  const followers = t('contactDrawer.profile.followersLabel', {
    value: formatCompactNumber(followersCount, locale),
  });
  if (!isFiniteNumber(engagementRate)) return followers;
  return `${followers} · ${formatPercent(engagementRate, locale)} ER`;
};

type StatKind = ContactStatKind;

/** The statistics the plan withholds from a prospect; the follower count is not one. */
const WITHHELD_STAT_KINDS: ReadonlySet<StatKind> = new Set<StatKind>([
  'engagement',
  'reach',
  'avgLikes',
  'avgComments',
  'emv',
]);

/**
 * The creator's fit with the brand. A creator the brand was recommended reads
 * the recommendation's tier, the one the recommendations list shows, so both
 * surfaces agree; any other creator reads the tier of the brand-match score.
 */
const resolveAffinityTier = (profile: PlanScopedCreatorProfile): MatchTier | null =>
  profile.matchTier ?? matchScoreToTier(profile.matchScore);

/**
 * The statistics tiles. With `withhold`, a statistic the plan withholds keeps
 * its tile, reading "Hidden" rather than a value or "not measured".
 */
const buildStats = (
  profile: PlanScopedCreatorProfile,
  t: TFunction,
  locale: string,
  withhold = false
): ContactStat[] => {
  // Local partial profiles carry placeholders, so every creator-api metric reads
  // the no-data sentinel on them. Live averages distinguish measured zero from
  // unavailable values using the server's nullable fields.
  const live = !profile.isPartial;
  const avgLikes = live ? profile.avgLikes : null;
  const avgComments = live ? profile.avgComments : null;
  const reach = live ? nonZero(profile.reach) : null;
  const engagement = live
    ? formatEngagementRate(profile.engagementRate)?.replace('.', decimalSeparator(locale))
    : NO_DATA_SENTINEL;
  const affinity = formatBrandAffinityScore(profile.matchScore);
  const affinityTier = resolveAffinityTier(profile);

  const tiles: Array<[StatKind, ContactStat]> = [
    [
      'followers',
      {
        label: t('contactDrawer.profile.statLabels.followers'),
        value: formatCompactNumber(profile.followersCount, locale),
      },
    ],
    [
      'engagement',
      {
        label: t('contactDrawer.profile.statLabels.engagement'),
        value: engagement ?? t('contactDrawer.profile.unmeasured'),
        muted: !engagement,
      },
    ],
    [
      'brandAffinity',
      {
        label: t('contactDrawer.profile.statLabels.brandAffinity'),
        // The card draws the tier as the flame scale; the text stays the
        // reading of the cell for anything that does not draw it.
        value:
          affinity ??
          (affinityTier
            ? t(`recommendations.matchScore.tier.${affinityTier}.label`)
            : t('contactDrawer.profile.notCalculated')),
        muted: !affinityTier,
        ...(affinityTier ? { matchTier: affinityTier } : {}),
      },
    ],
    [
      'reach',
      {
        label: t('contactDrawer.profile.statLabels.reach'),
        value: formatCompactNumber(reach, locale),
      },
    ],
    [
      'avgLikes',
      {
        label: t('contactDrawer.profile.statLabels.avgLikes'),
        value: formatCompactNumber(avgLikes, locale),
      },
    ],
    [
      'avgComments',
      {
        label: t('contactDrawer.profile.statLabels.avgComments'),
        value: formatCompactNumber(avgComments, locale),
      },
    ],
    ['emv', estimatedMediaValueStat(profile, live, t, locale)],
  ];

  return tiles.map(([kind, stat]) =>
    withhold && WITHHELD_STAT_KINDS.has(kind)
      ? { kind, label: stat.label, value: t('planLocks.stat'), muted: true }
      : { kind, ...stat }
  );
};

const isNiche = (value: string | null | undefined): value is Niche =>
  !!value && (NICHE_VALUES as readonly string[]).includes(value);

/**
 * The header badges. Only what the source reported: no tier without a score or
 * a recommendation, no niche outside the shared taxonomy ("other" says nothing),
 * and the origin built from whichever of country and age bracket is known.
 *
 * The creator's own age bracket lives here rather than in the statistics: it
 * is not a measured metric. Like the country it comes from the catalogue row,
 * so it survives a partial profile.
 */
const buildFacts = (profile: PlanScopedCreatorProfile, locale: string): ContactProfileFacts => {
  const country = profile.country ? geoLabel(profile.country, locale) : null;
  const age = formatAgeBucket(profile.ageBucket);
  const origin = [country, age].filter((part): part is string => !!part).join(' · ');
  return {
    isVerified: profile.isVerified === true,
    matchTier: resolveAffinityTier(profile),
    niche: isNiche(profile.category) && profile.category !== 'other' ? profile.category : null,
    origin: origin || null,
  };
};

/**
 * Map a `CreatorProfile` into a single `ContactProfile`. The drawer's platform
 * toggle is honoured by `platform`: the profile query asks the backend for the
 * identity's primary (highest-follower) profile on the selected platform.
 */
export function mapCreatorProfileToContact(
  profile: PlanScopedCreatorProfile,
  t: TFunction,
  locale: string
): ContactProfile {
  const platform = platformOf(profile.platform);
  // A prospect the plan masks has no handle and no link to its account.
  const handle = profile.username ?? '';
  const statsLocked = isStatsLocked(profile);
  // A partial profile has no measured engagement/audience — surface follower
  // count only and let the view render the rest as an explicit no-data state.
  const hasLiveMetrics = !profile.isPartial;

  return {
    platform,
    handle,
    profilePictureUrl: profile.profilePictureUrl,
    followersLabel: formatFollowersLabel(
      profile.followersCount,
      hasLiveMetrics && !statsLocked ? profile.engagementRate : null,
      t,
      locale
    ),
    contextTitle: t('contactDrawer.profile.contextTitle', { platform: PLATFORM_LABEL[platform] }),
    brandColor: PLATFORM_BRAND_COLOR[platform],
    profileUrl: handle ? `${PLATFORM_BASE_URL[platform]}${handle}` : '',
    stats: buildStats(profile, t, locale, statsLocked),
    audience: mapAudience(profile.audienceInsights),
    brandCollaborations: toBrandCollaborations(profile.brandCollaborations),
    hasLiveMetrics,
    facts: buildFacts(profile, locale),
    ...(profile.registered === true && !profile.locked?.identity
      ? {
          declaredProfile: {
            registered: true,
            city: profile.city,
            topics: profile.topics,
            contentFormats: profile.contentFormats,
            contentLanguages: profile.contentLanguages,
            opportunityInterests: profile.opportunityInterests,
            availability: profile.availability,
            unavailableUntil: profile.unavailableUntil,
          },
        }
      : {}),
    ...(profile.ugcOffer ? { ugcOffer: profile.ugcOffer } : {}),
    ...(profile.locked && { locked: profile.locked }),
  };
}

/** Empty audience for a rail-only profile row (no live audience slice loaded). */
export const EMPTY_CONTACT_AUDIENCE: ContactAudience = mapAudience(undefined);

/**
 * Build a rail `ContactProfile` row from the lightweight summary projection (no
 * stats / audience). The rail (`ContactProfilesList`) only reads platform,
 * handle, follower label and brand colour; the profile view fills the rest from
 * its own live slice via `mapCreatorProfileToContact`.
 */
export function buildContactProfileRow(
  profile: PlanScopedContactSummaryDto['socialProfiles'][number],
  t: TFunction,
  locale: string
): ContactProfile {
  // `ContactSummarySocialProfile.engagementRate` is in percentage points while
  // `formatFollowersLabel` expects the canonical 0-1 ratio; see that field's doc
  // in `@fluenceur/types` for why the two DTOs differ. Convert at this
  // boundary rather than teaching the shared formatter a second unit.
  const engagementRatio = isFiniteNumber(profile.engagementRate)
    ? profile.engagementRate / 100
    : null;
  return {
    platform: profile.platform,
    handle: profile.handle ?? '',
    followersLabel: formatFollowersLabel(profile.followersCount, engagementRatio, t, locale),
    contextTitle: t('contactDrawer.profile.contextTitle', {
      platform: PLATFORM_LABEL[profile.platform],
    }),
    brandColor: PLATFORM_BRAND_COLOR[profile.platform],
    profileUrl: profile.handle ? `${PLATFORM_BASE_URL[profile.platform]}${profile.handle}` : '',
    stats: [],
    audience: EMPTY_CONTACT_AUDIENCE,
    // The summary projection carries no collaboration signals; the profile view
    // fills them from its own live slice.
    brandCollaborations: [],
  };
}
